import React, { createContext, useContext, useState, useEffect } from 'react';
import defaultConfig from './default-config.json';
import configPromise from './config-loader';
import createTheme from '../styles/theme'; // Import the theme creator

// Generate the initial theme based on the default config
const initialTheme = createTheme(defaultConfig);

// Create context with a default shape including loading and error states
export const ConfigContext = createContext({
  config: defaultConfig,
  theme: initialTheme, // Add theme to context default
  loading: true,
  error: null,
});

/**
 * Provider component for center configuration
 */
export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [theme, setTheme] = useState(initialTheme); // Add theme state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Add error state

  useEffect(() => {
    // Load the configuration when component mounts
    configPromise.then(loadedConfig => {
      setConfig(loadedConfig);
      setTheme(createTheme(loadedConfig)); // Generate and set dynamic theme
      setError(null); // Clear previous errors
    }).catch(error => {
      console.error('Failed to load configuration:', error);
      setError(error); // Set error state
      // Keep defaultConfig in case of error
    }).finally(() => {
      setLoading(false); // Set loading false whether success or error
    });
  }, []);

  // Provide config, loading, and error states via context
  const contextValue = {
    config,
    theme, // Provide theme in context
    loading,
    error,
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

// Custom hook to use the config
export const useConfig = () => useContext(ConfigContext);
